import { Suspense, lazy, useEffect, useRef, useState } from "react";

const ContactForm = lazy(() =>
  import("./ContactForm").then(m => ({ default: m.ContactForm }))
);

/**
 * Defers the contact form bundle until it is close to the viewport (or the
 * visitor interacts), so the homepage's initial JS stays small. Once loaded it
 * behaves exactly like <ContactForm />, including validation, lead POST and
 * Google Ads / GA4 conversion events.
 */
export function LazyContactForm(props: { defaultService?: string; formName?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShow(true);
      return;
    }
    const io = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin: "600px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show]);

  return (
    <div ref={ref}>
      {show ? (
        <Suspense fallback={<FormSkeleton />}>
          <ContactForm {...props} />
        </Suspense>
      ) : (
        <FormSkeleton />
      )}
    </div>
  );
}

function FormSkeleton() {
  // Same outer box as the real form so layout never shifts (CLS stays 0).
  return (
    <div
      aria-hidden="true"
      className="rounded-xl border border-border bg-white p-5 md:p-7 shadow-[var(--shadow-card)] space-y-4"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <SkeletonField />
        <SkeletonField />
      </div>
      <SkeletonField />
      <SkeletonField />
      <SkeletonField />
      <div>
        <div className="h-5 w-24 rounded bg-muted mb-1.5" />
        <div className="h-[98px] w-full rounded-md border border-input bg-muted/40" />
      </div>
      <div className="h-12 w-full rounded-md bg-muted" />
      <div className="h-8" />
    </div>
  );
}

function SkeletonField() {
  return (
    <div>
      <div className="h-5 w-28 rounded bg-muted mb-1.5" />
      <div className="h-12 w-full rounded-md border border-input bg-muted/40" />
    </div>
  );
}
